import React, { Suspense, lazy } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useParams } from 'react-router-dom';
import { Toaster } from '@/components/ui/sonner';
import { AuthProvider } from './context/AuthContext';
import { UnsavedChangesProvider } from './context/UnsavedChangesContext';
import { ChatProvider } from './context/ChatContext';
import ProtectedRoute from './components/ProtectedRoute';
import Layout from './components/Layout';
import Login from './pages/Login';
import StorefrontLayout from './pages/storefront/StorefrontLayout';
import { isStorefrontHost, storeUrl } from './pages/storefront/storeBase';
import DocumentTitle from './components/DocumentTitle';
import './App.css';

// Code-split every page: each route downloads its own chunk on demand instead
// of shipping the whole app (POS + storefront) in one bundle on first load.
const POS = lazy(() => import('./pages/POS'));
const Settings = lazy(() => import('./pages/Settings'));
const NewBatch = lazy(() => import('./pages/NewBatch'));
const Queue = lazy(() => import('./pages/Queue'));
const BatchDetails = lazy(() => import('./pages/BatchDetails'));
const Repricer = lazy(() => import('./pages/Repricer'));
const EmployeeZone = lazy(() => import('./pages/EmployeeZone'));
const Analytics = lazy(() => import('./pages/Analytics'));
const Orders = lazy(() => import('./pages/Orders'));
const Events = lazy(() => import('./pages/Events'));
const Inventory = lazy(() => import('./pages/Inventory'));
const DailyTasks = lazy(() => import('./pages/DailyTasks'));
const Customers = lazy(() => import('./pages/Customers'));
const StorefrontHome = lazy(() => import('./pages/storefront/StorefrontHome'));
const StorefrontCatalog = lazy(() => import('./pages/storefront/StorefrontCatalog'));
const StorefrontEvents = lazy(() => import('./pages/storefront/StorefrontEvents'));
const StorefrontProduct = lazy(() => import('./pages/storefront/StorefrontProduct'));
const StorefrontSell = lazy(() => import('./pages/storefront/StorefrontSell'));
const StorefrontHotlist = lazy(() => import('./pages/storefront/StorefrontHotlist'));
const StorefrontAbout = lazy(() => import('./pages/storefront/StorefrontStaticPages').then((m) => ({ default: m.StorefrontAbout })));
const StorefrontShipping = lazy(() => import('./pages/storefront/StorefrontStaticPages').then((m) => ({ default: m.StorefrontShipping })));
const StorefrontContact = lazy(() => import('./pages/storefront/StorefrontStaticPages').then((m) => ({ default: m.StorefrontContact })));
const StorefrontAccountPage = lazy(() => import('./pages/storefront/StorefrontAccountPage'));
const StorefrontAuthPage = lazy(() => import('./pages/storefront/StorefrontAuthPage'));
const StorefrontResetPassword = lazy(() => import('./pages/storefront/StorefrontResetPassword'));

const PageFallback = () => (
  <div className="flex items-center justify-center min-h-[50vh]" data-testid="page-loading-fallback">
    <div className="w-8 h-8 border-2 border-current border-t-transparent rounded-full animate-spin opacity-40" />
  </div>
);

// Redirect legacy/printed QR labels that used the PLURAL `/products/<handle>`
// path onto the real SINGULAR storefront product route, preserving the handle.
// Without this, already-printed stickers hit the catch-all and lose the handle
// (redirecting to home — the original "QR goes home" bug for old labels).
const LegacyProductRedirect = () => {
  const { handle } = useParams();
  return <Navigate to={storeUrl(`/product/${handle}`)} replace />;
};

// Storefront page routes, reused whether the storefront is mounted at the
// root (apex domain) or under /store (preview).
const storefrontChildren = (
  <>
    <Route index element={<StorefrontHome />} />
    <Route path="catalog" element={<StorefrontCatalog />} />
    <Route path="events" element={<StorefrontEvents />} />
    <Route path="product/:handle" element={<StorefrontProduct />} />
    {/* Legacy printed-label support: /products/<handle> -> /product/<handle> */}
    <Route path="products/:handle" element={<LegacyProductRedirect />} />
    <Route path="sell" element={<StorefrontSell />} />
    <Route path="hotlist" element={<StorefrontHotlist />} />
    <Route path="about" element={<StorefrontAbout />} />
    <Route path="shipping" element={<StorefrontShipping />} />
    <Route path="contact" element={<StorefrontContact />} />
    <Route path="account" element={<StorefrontAccountPage />} />
    <Route path="account/login" element={<StorefrontAuthPage />} />
    <Route path="account/register" element={<StorefrontAuthPage />} />
    <Route path="account/reset" element={<StorefrontResetPassword />} />
    {/* Unknown storefront paths fall back to the storefront home. */}
    <Route path="*" element={<Navigate to={storeUrl('/')} replace />} />
  </>
);

// POS (admin) app — entirely namespaced under /pos (paths are relative because
// this tree is mounted at "/pos/*"). Root landing is /pos.
const PosRoutes = () => (
  <ProtectedRoute>
    <ChatProvider>
    <Layout>
      <Routes>
        {/* POS dashboard lives at /pos */}
        <Route index element={
          <ProtectedRoute permission="use_pos">
            <POS />
          </ProtectedRoute>
        } />
        <Route path="settings" element={
          <ProtectedRoute permission="manage_settings">
            <Settings />
          </ProtectedRoute>
        } />
        <Route path="new-batch" element={
          <ProtectedRoute permission="create_batches">
            <NewBatch />
          </ProtectedRoute>
        } />
        <Route path="queue" element={<Queue />} />
        <Route path="daily-tasks" element={<DailyTasks />} />
        <Route path="events" element={<Events />} />
        <Route path="batch/:id" element={<BatchDetails />} />
        <Route path="repricer" element={
          <ProtectedRoute permission="access_repricer">
            <Repricer />
          </ProtectedRoute>
        } />
        <Route path="inventory" element={
          <ProtectedRoute permission="view_inventory">
            <Inventory />
          </ProtectedRoute>
        } />
        <Route path="employee-zone" element={<EmployeeZone />} />
        <Route path="analytics" element={
          <ProtectedRoute permission="view_analytics">
            <Analytics />
          </ProtectedRoute>
        } />
        <Route path="orders" element={
          <ProtectedRoute permission="use_pos">
            <Orders />
          </ProtectedRoute>
        } />
        <Route path="customers" element={
          <ProtectedRoute permission="view_customers">
            <Customers />
          </ProtectedRoute>
        } />
        {/* Unknown /pos/* path -> POS dashboard */}
        <Route path="*" element={<Navigate to="/pos" replace />} />
      </Routes>
    </Layout>
    </ChatProvider>
  </ProtectedRoute>
);

function App() {
  return (
    <div className="App">
      <DocumentTitle />
      <BrowserRouter>
        <AuthProvider>
          <UnsavedChangesProvider>
          {/*
            Routing model (all hosts):
              - Storefront mounts at "/" on the apex (radgameshop.com) or "/store"
                on preview — `storeUrl()` keeps storefront links correct.
              - The POS app is ALWAYS under "/pos" (e.g. /pos, /pos/inventory).
                Login is at /pos/login. Root paths (/inventory, /orders, …) are
                reserved for the storefront.
          */}
          <Suspense fallback={<PageFallback />}>
          <Routes>
            <Route path="/pos/login" element={<Login />} />
            <Route path="/login" element={<Navigate to="/pos/login" replace />} />
            <Route path={isStorefrontHost ? '/' : '/store'} element={<StorefrontLayout />}>
              {storefrontChildren}
            </Route>
            <Route path="/pos/*" element={<PosRoutes />} />
            {/* On non-apex (preview) the root isn't the storefront, so send it
                to the POS for convenience. */}
            {!isStorefrontHost && <Route path="/" element={<Navigate to="/pos" replace />} />}
            {/* Any other unknown top-level path -> storefront home (root paths
                are reserved for the storefront). */}
            <Route path="*" element={<Navigate to={storeUrl('/')} replace />} />
          </Routes>
          </Suspense>
          </UnsavedChangesProvider>
        </AuthProvider>
      </BrowserRouter>
      <Toaster position="top-right" />
    </div>
  );
}

export default App;
